'use client'
import { Box, Flex, Group, Skeleton } from '@mantine/core'
import { useTranslations } from 'next-intl'
import { Suspense, useMemo } from 'react'

import { useCoinContext } from '@/app/[locale]/price/[slug]/_contexts/CoinContext'
import { SectionErrorBoundary } from '@/components/error/boundary'
import Swiper from '@/components/Swiper'
import { useSsrContext } from '@/contexts/ssr'
import { isEEARegion } from '@/i18n/country'
import { CmsCampaign } from '@/services/coin/cmsCampaign'
import { ResolvedRewardsCampaign } from '@/services/coin/rewardsCampaign'
import { useCryptoBasket, useYieldProducts } from '@/services/invest'
import { InvestCardType } from '@/types/invest'
import { isCoinVisible, isVisibleForCountry } from '@/utils/campaign'

import CmsCampaignCard from '../CampaignBanners/CmsCampaignCard'
import RewardsCampaignCard from '../CampaignBanners/RewardsCampaignCard'
import InvestCard from './InvestCard'

const MAX_CAMPAIGN_ITEMS = 3

type CampaignSwiperItem = {
  kind: 'campaign'
  campaign: CmsCampaign
}

type RewardsSwiperItem = {
  kind: 'rewards'
  campaign: ResolvedRewardsCampaign
}

type InvestSwiperItem = {
  kind: 'invest'
  type: InvestCardType
  title: string
  link: string
  image?: string
  updatePercentage?: string
  updateTime?: string
  tagIcons?: any[]
  badge?: string
  buttonText?: string
}

type SwiperItem = CampaignSwiperItem | RewardsSwiperItem | InvestSwiperItem

function InvestFallback() {
  return (
    <Box mt={80} style={{ overflow: 'hidden' }}>
      {/* Title and controller area */}
      <Flex justify="space-between" align="center" mb={16}>
        <Skeleton
          h={32}
          w={160}
          style={{ backgroundColor: 'var(--bg-surface-secondary)', borderRadius: 40 }}
        />
      </Flex>

      {/* Content cards area */}
      <Box style={{ overflow: 'hidden' }}>
        <Group gap="md" wrap="nowrap">
          {[0, 1, 2, 3].map(index => (
            <Skeleton
              key={index}
              h={240}
              w={190}
              style={{
                backgroundColor: 'var(--bg-surface-secondary)',
                borderRadius: 16,
                flexShrink: 0,
              }}
            />
          ))}
        </Group>
      </Box>
    </Box>
  )
}

function InvestContent({
  locale,
  rewardsCampaigns,
  cmsCampaigns,
}: {
  locale: string
  rewardsCampaigns: ResolvedRewardsCampaign[]
  cmsCampaigns: CmsCampaign[]
}) {
  const { coin } = useCoinContext()
  const { countryCode, regionCode } = useSsrContext()

  const { data: basket } = useCryptoBasket(coin?.symbol || 'BTC')
  const { data: yieldProductsData } = useYieldProducts(coin?.symbol || 'BTC')
  const t = useTranslations('invest')

  const swiperList = useMemo<SwiperItem[]>(() => {
    const list: SwiperItem[] = []
    const now = Date.now()

    // CMS campaigns: coin-matched, IP-visible, not expired
    const cmsMatched = cmsCampaigns
      .filter(
        c =>
          c.campaignBanner?.campaignTitle &&
          isCoinVisible(c, coin.symbol) &&
          isVisibleForCountry(c.allowedIps, c.restrictedIps, countryCode, regionCode) &&
          (c.noExpiryDate || !c.campaignEndDate || new Date(c.campaignEndDate).getTime() > now),
      )
      .slice(0, MAX_CAMPAIGN_ITEMS)
      .map((campaign): CampaignSwiperItem => ({ kind: 'campaign', campaign }))

    list.push(...cmsMatched)

    // Rewards API backfill if < 3 CMS campaigns
    const remaining = MAX_CAMPAIGN_ITEMS - cmsMatched.length
    if (remaining > 0) {
      const rewardsMatched = rewardsCampaigns
        .filter(
          c =>
            c.status === 'ACTIVE' &&
            c.coins?.some(s => s.toUpperCase() === coin.symbol.toUpperCase()) &&
            (c.end_time === null || new Date(c.end_time).getTime() > now),
        )
        .slice(0, remaining)
        .map(
          (campaign): RewardsSwiperItem => ({
            kind: 'rewards',
            campaign,
          }),
        )

      list.push(...rewardsMatched)
    }

    // Add basket card
    if (basket?.crypto_baskets.length > 0) {
      const firstBasket = basket.crypto_baskets[0]
      list.push({
        kind: 'invest',
        type: InvestCardType.Basket,
        title: firstBasket.title,
        image: firstBasket.banner_url,
        updateTime: firstBasket.updated_at,
        tagIcons: firstBasket.coins,
        badge: t('basket.mostTraded'),
        link: 'https://web.crypto.com/signup',
      })
    }

    // Add yield product cards
    const yieldCards: InvestSwiperItem[] = [
      {
        kind: 'invest',
        type: InvestCardType.Stake,
        title: t('stake.title', { symbol: coin?.symbol }),
        buttonText: t('stake.buttonText', { symbol: coin?.symbol }),
        link: 'https://crypto.com/earn',
      },
      {
        kind: 'invest',
        type: InvestCardType.Earn,
        title: t('earn.title'),
        buttonText: t('earn.buttonText', { symbol: coin?.symbol }),
        link: 'https://crypto.com/earn/',
      },
      {
        kind: 'invest',
        type: InvestCardType.Recurring,
        title: t('recurring.title'),
        buttonText: t('recurring.buttonText'),
        image: '',
        link: 'https://web.crypto.com/signup',
      },
      {
        kind: 'invest',
        type: InvestCardType.Exchange,
        title: t('exchange.title'),
        buttonText: t('exchange.buttonText'),
        image: '',
        link: 'https://crypto.com/exchange',
      },
    ]

    yieldCards.forEach(item => {
      const { type, title, buttonText, image, link } = item
      // Only show Exchange card when isRestricted is false
      if (type === InvestCardType.Exchange) {
        if (isEEARegion(locale, countryCode)) return
        list.push(item)
        return
      }

      const data = yieldProductsData?.find(d => d.product_type === type)

      if (data || type === InvestCardType.Recurring) {
        const apr = data ? Number(data.apr) * 100 : null
        const finalTitle = apr
          ? `${title} ${apr}% p.a.${type === InvestCardType.Earn ? ` ${t('earn.withRegularPayouts')}` : ''}`
          : title

        list.push({
          kind: 'invest',
          type,
          title: finalTitle,
          image: image || data?.icon_url || '',
          buttonText,
          link,
        })
      }
    })

    return list
  }, [
    basket?.crypto_baskets,
    t,
    coin?.symbol,
    yieldProductsData,
    locale,
    countryCode,
    regionCode,
    cmsCampaigns,
    rewardsCampaigns,
  ])

  if (swiperList.length === 0) {
    return null
  }

  return (
    <Box mt={80}>
      <Swiper
        title={t('title', { coinName: coin?.name })}
        swiperList={swiperList}
        renderSwiper={(item, idx) =>
          item.kind === 'campaign' ? (
            <CmsCampaignCard key={idx} campaign={item.campaign} locale={locale} />
          ) : item.kind === 'rewards' ? (
            <RewardsCampaignCard key={idx} campaign={item.campaign} locale={locale} />
          ) : (
            <InvestCard key={idx} {...item} />
          )
        }
      />
    </Box>
  )
}

function Invest({
  locale,
  rewardsCampaigns,
  cmsCampaigns,
}: {
  locale: string
  rewardsCampaigns: ResolvedRewardsCampaign[]
  cmsCampaigns: CmsCampaign[]
}) {
  return (
    <Suspense fallback={<InvestFallback />}>
      <InvestContent
        locale={locale}
        rewardsCampaigns={rewardsCampaigns}
        cmsCampaigns={cmsCampaigns}
      />
    </Suspense>
  )
}

export default SectionErrorBoundary.with(Invest)
